/**
 * CARs by vendor over the trailing six months.
 *
 * Sequential encoding — one hue, light to dark, magnitude only. Cells carry the
 * number as well as the shade, so the value never depends on reading color.
 * Clicking a vendor filters the whole report.
 *
 * Transplanted from the reference implementation. The `months` axis now comes
 * from the `matrix_months` dataset rather than a `months[]` field beside the
 * rows — same list, same purpose: it is what lets a month with no CARs still get
 * a column, which long-form rows cannot express on their own.
 */

import { useMemo, useState } from 'react'

import { monthLabel, monthShort } from '../shared/format'
import type { Keyed } from '../shared/rows'
import { count, text } from '../shared/rows'

/** Sequential blue ramp, lightest (near zero) to darkest. */
const RAMP = [
  'var(--seq-100)',
  'var(--seq-200)',
  'var(--seq-300)',
  'var(--seq-400)',
  'var(--seq-500)',
  'var(--seq-600)',
]

/** Ink that clears contrast against the fill it sits on. */
function inkFor(step: number): string {
  return step >= 3 ? '#ffffff' : '#0b0b0b'
}

interface Props {
  rows: readonly Keyed[]
  months: readonly string[]
  onSelectVendor: (supplierId: string) => void
}

export function VendorMatrix({ rows, months, onSelectVendor }: Props) {
  const [sortBy, setSortBy] = useState<'total' | 'name'>('total')

  const { vendors, lookup, max, columnTotals } = useMemo(() => {
    const lookup = new Map<string, number>()
    const totals = new Map<string, { name: string | null; total: number }>()
    const columnTotals = new Map<string, number>()

    for (const row of rows) {
      const supplierId = String(count(row.supplier_id))
      const month = text(row.month) ?? ''
      const cars = count(row.car_count)
      lookup.set(`${supplierId}|${month}`, cars)
      const entry = totals.get(supplierId) ?? {
        name: text(row.supplier_name),
        total: 0,
      }
      entry.total += cars
      totals.set(supplierId, entry)
      columnTotals.set(month, (columnTotals.get(month) ?? 0) + cars)
    }

    const vendors = [...totals.entries()].map(([id, v]) => ({ id, ...v }))
    vendors.sort((a, b) =>
      sortBy === 'total'
        ? b.total - a.total || (a.name ?? '').localeCompare(b.name ?? '')
        : (a.name ?? '').localeCompare(b.name ?? ''),
    )

    return {
      vendors,
      lookup,
      max: Math.max(1, ...rows.map((r) => count(r.car_count))),
      columnTotals,
    }
  }, [rows, sortBy])

  if (vendors.length === 0) {
    return <div className="state">No CARs in this six-month window.</div>
  }

  /** Count to ramp index. Zero is never shaded — absence should look absent. */
  const stepFor = (cars: number) =>
    cars === 0 ? -1 : Math.min(RAMP.length - 1, Math.floor(((cars - 1) / max) * RAMP.length))

  return (
    <>
      <div className="matrix-scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th className="vendor-head">
                <button
                  className="link-button"
                  onClick={() => {
                    setSortBy(sortBy === 'total' ? 'name' : 'total')
                  }}
                >
                  Vendor {sortBy === 'total' ? '(by volume)' : '(A–Z)'}
                </button>
              </th>
              {months.map((m) => (
                <th key={m} title={monthLabel(m)}>
                  {monthShort(m)}
                </th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor) => (
              <tr key={vendor.id}>
                <td className="vendor-cell">
                  <button
                    className="vendor-button"
                    onClick={() => {
                      onSelectVendor(vendor.id)
                    }}
                    title={`Filter the report to ${vendor.name ?? 'this vendor'}`}
                  >
                    {vendor.name ?? `Vendor ${vendor.id}`}
                  </button>
                </td>
                {months.map((month) => {
                  const cars = lookup.get(`${vendor.id}|${month}`) ?? 0
                  const step = stepFor(cars)
                  return (
                    <td
                      key={month}
                      className="count-cell"
                      style={{
                        background: step < 0 ? 'transparent' : (RAMP[step] ?? 'transparent'),
                        color: step < 0 ? 'var(--text-muted)' : inkFor(step),
                      }}
                      title={`${vendor.name ?? 'Vendor'} — ${monthLabel(month)}: ${String(cars)}`}
                    >
                      {cars === 0 ? '·' : cars}
                    </td>
                  )
                })}
                <td className="total-cell">{vendor.total}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td className="vendor-cell" style={{ textAlign: 'left', padding: '6px 2px' }}>
                All vendors
              </td>
              {months.map((m) => (
                <td key={m}>{columnTotals.get(m) ?? 0}</td>
              ))}
              <td>{vendors.reduce((sum, v) => sum + v.total, 0)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="legend-scale" style={{ marginTop: 12 }}>
        <span>Fewer</span>
        {RAMP.map((color) => (
          <span key={color} className="legend-swatch" style={{ background: color }} />
        ))}
        <span>More CARs</span>
      </div>
    </>
  )
}
